import { arrow, autoUpdate, computePosition, flip, FlipOptions, offset, shift } from "@floating-ui/dom";
import { AlignmentUtil } from "@mcleod/core";
import { AnchorMutation } from "../../Anchor";
import { AnchoredComponentPositioner, AnchoredComponentPositionerProps, AnchoredComponentType } from "../AnchoredComponentPositioner";
import { FloatingUI, POSITION_AND_ALIGNMENTS } from "./FloatingUI";

const viewportPadding = 10;

interface FloatingUIMutation extends AnchorMutation {
    middleWareData: any;
}

/**
 * Uses the Floating UI library to position anchored components (tooltips or overlays) relative to an anchor element.
 * Supports dynamic repositioning, fallback placements, and pointer (arrow) positioning.
 */
export class FloatingUIPositioner extends AnchoredComponentPositioner {

    protected constructor(props: AnchoredComponentPositionerProps, componentType: AnchoredComponentType) {
        super(props, componentType);
    }

    listenForPositionUpdates(): void {
        this.anchoredComponent.addMountListener(() => {
            const cleanup = autoUpdate(this.anchorElement, this.anchoredComponent._element, () => this.computeAndUpdatePosition());
            this.anchoredComponent.addUnmountListener(() => cleanup());
        });
    }

    override async computePosition(): Promise<FloatingUIMutation> {
        const placement = FloatingUI.getPlacementfromAnchor(this.anchorProps);

        const flipOptions: FlipOptions = {padding: viewportPadding};
        if (this.isTooltipType) {
            flipOptions.fallbackPlacements = FloatingUI.getPlacementFallbacks(placement);
        }

        let spacing = 4;
        if (this.pointerSize > 0) {
            spacing = this.pointerSize + 2;
        }

        const result = await computePosition(this.anchorElement, this.componentElement, {
            placement,
            middleware: [
                // Provides spacing between the anchored component and the anchor.
                offset(spacing),

                // Keeps the component within the visible viewport by shifting it as needed.
                // The viewport padding prevents the component from being pinned to the viewport edge.
                shift({ padding: viewportPadding }),

                // Attempts to flip the position to another placement when the component
                // cannot fit in the preferred placement.
                flip(flipOptions),

                // calculates the pointers position (if present) so it's centered on the anchor
                arrow({ element: this.pointer?._element }),
            ],
        });

        const positionAndAlign = POSITION_AND_ALIGNMENTS[result.placement];
        return {
            newRect: new DOMRect(result.x, result.y),
            newAnchor:{
                ...this.anchorProps,
                position: positionAndAlign.position,
                align: positionAndAlign.align
            },
            middleWareData: result.middlewareData
        };
    }

    override getAdjustedPointerStyle(): Partial<CSSStyleDeclaration> {
        const result = super.getAdjustedPointerStyle();
        const mutation = this.currentMutation as FloatingUIMutation;
        const middlewareData = mutation.middleWareData;
        if (middlewareData?.arrow) {
            const {x, y} = middlewareData.arrow;
            const pointerSide = AlignmentUtil.getOppositeAlignment(mutation.newAnchor.position);

            Object.assign(result, {
                left: x != null ? `${x}px` : "",
                top: y != null ? `${y}px` : "",
                right: "",
                bottom: "",
                [pointerSide]: `-${this.pointerSize}px`,
              });
        }
        return result;
    }
}
